import React, { Fragment, useContext, useEffect, useState } from "react";
import { Button, GridLegacy as Grid, IconButton } from "@mui/material";
import { FiFolder, FiPlus, FiTrash } from "react-icons/fi";
import { api, Product, ProductTypes } from "../../logic/api";

import SwitchField from "../editable/EditableField/fields/SwitchField";
import flex from "../../styles/flex";
import { formatCamel, isAdmin } from "../../utility/utils";
import { SessionContext } from "../../utility/useSession";
import TreeView, { ITreeViewCommand, ITreeViewState, TreeViewCommand, TreeViewContext } from "../treeView/TreeView";
import ProductsTreeViewLoader from "../treeView/loaders/ProductsTreeViewLoader";
import NotchedCard from "../cards/NotchedCard/NotchedCard";
import EditableField, { EditableFieldTypes } from "../editable/EditableField/EditableField";
import EditableGroup, { EditableGroupType } from "../editable/EditableGroup/EditableGroup";
import { IDropDownListItem } from "../editable/EditableField/fields/DropDownField";
import ErrorDialog from "../notifications/errorDialog/ErrorDialog";
import ConfirmationDialog from "../notifications/confirmationDialog/ConfirmationDialog";
import { ProgressDialog } from "../notifications/progressDialog/progressDialog";
import { ReleaseFlag, useReleaseManagement } from "../../utility/feature-management";
import { useTranslation } from "react-i18next";
import { useUserPermissions } from "@/hooks/useUserPermissions";

const ProductManager = () => {

  const session = useContext(SessionContext);
  const [parent, setParent] = useState<Product | null>();
  const { t } = useTranslation();
  const { hasPermission } = useUserPermissions();

  const requestSaveProduct = api.hooks.products.save();
  const requestDeleteProduct = api.hooks.products.delete();
  const [showDeleteProduct, setShowDeleteProduct] = useState(false);

  const [treeState, setTreeState] = useState<ITreeViewState>({
    treeRootId: 0,
    selectedRecord: null,
    flags: {}
  });

  const carParkId = (session.current.site == null || session.current.site.home == null) ? null : session.current.site.home.id;
  const [currentCarParkId, setCurrentCarParkId] = useState(carParkId);

  const isAdditionalTaxRatesReleased = useReleaseManagement(ReleaseFlag.releaseAdditionalTaxRates);
  const isRbacReleased = useReleaseManagement(ReleaseFlag.releaseRbac);
  const isCapProductsSoldPerReturnDateReleased = useReleaseManagement(ReleaseFlag.releaseCapProductsSoldPerReturnDate);

  useEffect(() => {
    if (carParkId != currentCarParkId) {
      setCurrentCarParkId(carParkId);
      setTreeState({
        treeRootId: 0,
        selectedRecord: null,
        flags: {},
        command: {
          command: TreeViewCommand.reloadAll,

          payLoad: null
        }
      });
      setParent(null);
    }
  }, [carParkId]);

  const [productType, setProductType] = useState(treeState.selectedRecord == null ? ProductTypes.default : (treeState.selectedRecord as Product).productType);
  const [maxQtyPerBookingEnabled, setMaxQtyPerBookingEnabled] = useState(treeState.selectedRecord == null ? false : (treeState.selectedRecord as Product).maxQtyEnabled == true);
  const [maxQtyPerDayEnabled, setMaxQtyPerDayEnabled] = useState(treeState.selectedRecord == null ? false : (treeState.selectedRecord as Product).maxQtyPerDayEnabled == true);

  useEffect(() => {
    if (treeState.selectedRecord == null) {
      setProductType(ProductTypes.default);
      setMaxQtyPerBookingEnabled(false);
      setMaxQtyPerDayEnabled(false);
    } else {
      const selProdType = (treeState.selectedRecord as Product).productType;
      if (selProdType != productType) {
        setProductType(selProdType);
      }
      if (maxQtyPerBookingEnabled != (treeState.selectedRecord as Product).maxQtyEnabled == true) {
        setMaxQtyPerBookingEnabled((treeState.selectedRecord as Product).maxQtyEnabled == true);
      }
      if (maxQtyPerDayEnabled != (treeState.selectedRecord as Product).maxQtyPerDayEnabled == true) {
        setMaxQtyPerDayEnabled((treeState.selectedRecord as Product).maxQtyPerDayEnabled == true);
      }
    }

  }, [treeState.selectedRecord]);

  let editMode = EditableGroupType.switchableEditing;

  if (isRbacReleased && treeState) {
    editMode = treeState?.selectedRecord?.id == -1 ? EditableGroupType.switchableEditing
      : !hasPermission("bookingManagement.saleItems.update")
        ? EditableGroupType.none
        : EditableGroupType.switchableEditing;
  }


  const save = async (prod: Product) => {
    return new Promise<boolean>((accept) => {
      const isNew = prod.id == -1;
      const isSub = isNew == true && prod.parentId != null && prod.parentId != 0;

      let rootId: number | null = null;

      if (parent) {
        rootId = parent.rootId;
      }

      prod = { ...prod, rootId: rootId }

      requestSaveProduct.send(session.current.context, prod, (result) => {
        if (result == null) {
          accept(false);
          return;
        }

        let command: ITreeViewCommand = {
          command: TreeViewCommand.reloadAll,
          targetId: treeState.treeRootId,
        };

        if (isNew == false) {
          command = {
            command: TreeViewCommand.update,
            targetId: result.id,
            payLoad: { ...result }
          }
        }

        if (isSub == true) {
          command = {
            command: TreeViewCommand.reload,
            targetId: result.parentId,
            payLoad: { ...result }
          }
        }

        setTreeState({
          treeRootId: 0,
          selectedRecord: result,
          flags: treeState.flags,
          command: command
        });
        accept(true);
        return;
      });
    })

  }

  const deleteProduct = async () => {
    if (treeState.selectedRecord != null) {
      requestDeleteProduct.send(session.current.context, {
        id: treeState.selectedRecord.id
      }, (result) => {
        if (result != null) {
          setParent(null);
          if (treeState.selectedRecord != null) {
            setTreeState({
              treeRootId: 0,
              selectedRecord: null,
              flags: treeState.flags,
              command: {
                command: TreeViewCommand.remove,
                targetId: treeState.selectedRecord.id,
                payLoad: null
              }
            });
          }
        }
      });

    }
  }

  const singleTaxFields = <Fragment>
    <EditableField<Product, string>
      inputType={EditableFieldTypes.text}
      label={t("products.code")}
      onProvide={(data) => data.code}
      onChange={(data, value) => data.code = value}
      testId="products-code-field"
    />
    <EditableField<Product, number>
      inputType={EditableFieldTypes.money}
      label={t("products.standardRateTax")}
      onProvide={(data) => data.stdRate}

      onChange={(data, value) => data.stdRate = value}
      testId="products-standard-rate-tax-field"
    />
    <EditableField<Product, number>
      inputType={EditableFieldTypes.percentage}
      label={t("products.taxRate")}
      onProvide={(data) => data.vat}

      onChange={(data, value) => data.vat = value}
      testId="products-tax-rate-field"
    />
    <EditableField<Product, number>
      inputType={EditableFieldTypes.money}
      label={t("products.costOfSale")}
      onProvide={(data) => {
        return data.costOfSale || 0
      }}
      onChange={(data, value) => data.costOfSale = value}
      testId="products-cost-of-sale-field"
    />

    <EditableField<Product, boolean>
      inputType={EditableFieldTypes.switch}
      label={t("products.maxQuantEnabled")}
      onProvide={(data) => data.maxQtyEnabled == true}
      onChange={(data, value) => {
        data.maxQtyEnabled = value == true;
        setMaxQtyPerBookingEnabled(data.maxQtyEnabled);
      }}
      testId="products-max-quant-enabled-field"
    />

    <EditableField<Product, number>
      inputType={EditableFieldTypes.number}
      readOnly={maxQtyPerBookingEnabled == false}
      label={t("products.maxQuantBooking")}
      onProvide={(data) => data.maxQty}
      onChange={(data, value) => data.maxQty = value}
      testId="products-max-quant-booking-field"
    />


    <EditableField<Product, boolean>
      inputType={EditableFieldTypes.switch}
      label={t(isCapProductsSoldPerReturnDateReleased ? "products.maxQuantReturnDateEnabled" : "products.maxQuantDayEnabled")}
      onProvide={(data) => data.maxQtyPerDayEnabled == true}
      onChange={(data, value) => {
        data.maxQtyPerDayEnabled = value == true;
        setMaxQtyPerDayEnabled(data.maxQtyPerDayEnabled);
      }}
      testId="products-max-quant-day-enabled-field"
    />

    <EditableField<Product, number>
      readOnly={maxQtyPerDayEnabled == false}
      inputType={EditableFieldTypes.number}
      label={t(isCapProductsSoldPerReturnDateReleased ? "products.maxQuantReturnDateBooking" : "products.maxQuantDay")}
      onProvide={(data) => data.maxQtyPerDay}
      onChange={(data, value) => data.maxQtyPerDay = value}
      testId="products-max-quant-day-field"
    />

    <EditableField<Product, IDropDownListItem>
      inputType={EditableFieldTypes.terminalField}
      label={t("products.productType")}

      onProvide={(data) => {
        return {
          name: formatCamel(ProductTypes[data.productType]),
          value: data.productType == null ? "" : data.productType.toString()
        };
      }}
      onChange={(data, item) => {
        let val = parseInt((item.value ?? "").toString());
        if (isNaN(val)) {
          val = ProductTypes.default;
        }
        data.productType = val;
        if (val == ProductTypes.default) {
          data.maxQty = 0;
          data.maxQtyPerDay = 0;
        }
        setProductType(data.productType);
      }}
      onRequestData={async () => {
        const items: IDropDownListItem[] = [];
        for (const pt in ProductTypes) {
          const intVal = parseInt(ProductTypes[pt]);
          if (intVal != ProductTypes.walkIn && intVal != ProductTypes.overstay) {
            if (isNaN(parseInt(pt))) {
              items.push({ name: formatCamel(pt.toString()), value: ProductTypes[pt] });
            }
          }
        }
        return items;

      }}
      testId="products-product-type-field"
    />
    <EditableField<Product, number | undefined>
      inputType={EditableFieldTypes.number}
      label={t("packages.pickingOffset")}
      onProvide={(data) => data.pickingOffset}
      onChange={(data, value) => data.pickingOffset = value ? Math.max(value, 0) : undefined}
      testId="products-picking-offset-field"
    />
    <EditableField<Product, string>
      inputType={EditableFieldTypes.textMultiLine}
      label={t("general.description")}
      onProvide={(data) => data.description}
      onChange={(data, value) => data.description = value}
      testId="products-description-field"
    />
    <EditableField<Product, boolean>
      inputType={EditableFieldTypes.switch}
      label={t("products.productActive")}
      onProvide={(data) => data.active == true}
      onChange={(data, value) => data.active = value == true}
      testId="products-product-active-field"
    />
  </Fragment>

  const additionalTaxFields = <Fragment>
    <EditableField<Product, string>
      inputType={EditableFieldTypes.text}
      label={t("products.code")}
      onProvide={(data) => data.code}
      onChange={(data, value) => data.code = value}
      testId="products-code-field"
    />
    <EditableField<Product, number>
      inputType={EditableFieldTypes.money}
      label={t("products.standardRateTax")}
      onProvide={(data) => data.stdRate}

      onChange={(data, value) => data.stdRate = value}
      testId="products-standard-rate-tax-field"
    />
    {session.current.site?.preferences.vat_name && <EditableField<Product, number>
      inputType={EditableFieldTypes.percentage}
      label={`${session.current.site?.preferences.vat_name} Rate`}
      onProvide={(data) => data.vat ?? session.current.site?.preferences.vat_rate}
      onChange={(data, value) => data.vat = value}
      testId="products-tax-rate-field"
    />}
    <EditableField<Product, number>
      inputType={EditableFieldTypes.money}
      label={t("products.costOfSale")}
      onProvide={(data) => {
        return data.costOfSale || 0
      }}
      onChange={(data, value) => data.costOfSale = value}
      testId="products-cost-of-sale-field"
    />
    {session.current.site?.preferences.vat_name_2 ? <EditableField<Product, number>
      inputType={EditableFieldTypes.percentage}
      label={`${session.current.site?.preferences.vat_name_2} Rate`}
      onProvide={(data) => {
        if (data.vat2) {
          return data.vat2;
        }

        if (session.current.site?.preferences.vat_rate_2) {
          data.vat2 = session.current.site?.preferences.vat_rate_2;
          return session.current.site?.preferences.vat_rate_2;
        }

        //should never get here
        return 0;
      }}
      onChange={(data, value) => data.vat2 = value}
      testId="products-tax-rate-2-field"
    /> : <EditableField inputType={EditableFieldTypes.empty} label='' />
    }
    <EditableField<Product, boolean>
      inputType={EditableFieldTypes.switch}
      label={t("products.maxQuantEnabled")}
      onProvide={(data) => data.maxQtyEnabled == true}
      onChange={(data, value) => {
        data.maxQtyEnabled = value == true;
        setMaxQtyPerBookingEnabled(data.maxQtyEnabled);
      }}
      testId="products-max-quant-enabled-field"
    />


    <EditableField<Product, number>
      inputType={EditableFieldTypes.number}
      readOnly={maxQtyPerBookingEnabled == false}
      label={t("products.maxQuantBooking")}
      onProvide={(data) => data.maxQty}
      onChange={(data, value) => data.maxQty = value}
      testId="products-max-quant-booking-field"
    />


    <EditableField<Product, boolean>
      inputType={EditableFieldTypes.switch}
      label={t(isCapProductsSoldPerReturnDateReleased ? "products.maxQuantReturnDateEnabled" : "products.maxQuantDayEnabled")}
      onProvide={(data) => data.maxQtyPerDayEnabled == true}
      onChange={(data, value) => {
        data.maxQtyPerDayEnabled = value == true;
        setMaxQtyPerDayEnabled(data.maxQtyPerDayEnabled);
      }}
      testId="products-max-quant-day-enabled-field"
    />


    <EditableField<Product, number>
      readOnly={maxQtyPerDayEnabled == false}
      inputType={EditableFieldTypes.number}
      label={t(isCapProductsSoldPerReturnDateReleased ? "products.maxQuantReturnDateBooking" : "products.maxQuantDay")}
      onProvide={(data) => data.maxQtyPerDay}
      onChange={(data, value) => data.maxQtyPerDay = value}
      testId="products-max-quant-day-field"
    />

    <EditableField<Product, number | undefined>
      inputType={EditableFieldTypes.number}
      label={t("packages.pickingOffset")}
      onProvide={(data) => data.pickingOffset}
      onChange={(data, value) => data.pickingOffset = value ? Math.max(value, 0) : undefined}
      testId="products-picking-offset-field"
    />

    <EditableField<Product, IDropDownListItem>
      inputType={EditableFieldTypes.terminalField}
      label={t("products.productType")}

      onProvide={(data) => {
        return {
          name: formatCamel(ProductTypes[data.productType]),
          value: data.productType == null ? "" : data.productType.toString()
        };
      }}
      onChange={(data, item) => {
        let val = parseInt((item.value ?? "").toString());
        if (isNaN(val)) {
          val = ProductTypes.default;
        }
        data.productType = val;
        if (val == ProductTypes.default) {
          data.maxQty = 0;
          data.maxQtyPerDay = 0;
        }
        setProductType(data.productType);
      }}
      onRequestData={async () => {
        const items: IDropDownListItem[] = [];
        for (const pt in ProductTypes) {
          const intVal = parseInt(ProductTypes[pt]);
          if (intVal != ProductTypes.walkIn && intVal != ProductTypes.overstay) {
            if (isNaN(parseInt(pt))) {
              items.push({ name: formatCamel(pt.toString()), value: ProductTypes[pt] });
            }
          }
        }
        return items;

      }}
      testId="products-product-type-field"
    />

    <EditableField<Product, boolean>
      inputType={EditableFieldTypes.switch}
      label={t("products.productActive")}
      onProvide={(data) => data.active == true}
      onChange={(data, value) => data.active = value == true}
      testId="products-product-active-field"
    />

    <EditableField<Product, string>
      inputType={EditableFieldTypes.textMultiLine}
      label={t("general.description")}
      onProvide={(data) => data.description}
      onChange={(data, value) => data.description = value}
      testId="products-description-field"
    />

  </Fragment>

  return (
    <div>
      <ErrorDialog err={requestSaveProduct.err}></ErrorDialog>
      <ProgressDialog status={requestSaveProduct.status}></ProgressDialog>
      <ErrorDialog err={requestDeleteProduct.err}></ErrorDialog>
      <ProgressDialog status={requestDeleteProduct.status}></ProgressDialog>
      <ConfirmationDialog
        title={t("products.confirmDelete")}
        message={t("products.productDeleteConfirm")}
        show={showDeleteProduct}
        onClose={(confirm) => {
          setShowDeleteProduct(false);
          if (confirm == true) {
            deleteProduct();
          }
        }}
      ></ConfirmationDialog>
      <div style={{ padding: 10, textAlign: "right" }}>
        <div style={flex.flexRows}>


          <div style={flex.flexFill}>

          </div>

          {isAdmin(session) && (
            <Fragment>
              <div style={flex.flexAuto}>
                <SwitchField onChange={(value) => {
                  setTreeState({
                    ...treeState,
                    flags: {
                      ...treeState.flags,
                      includeInactive: value
                    },
                    selectedRecord: null
                  });
                }} value={({ includeInactive: false, ...treeState.flags }).includeInactive == true} data-testid="products-include-inactive-switch" ></SwitchField>
              </div>
              <div style={flex.flexAuto}>
                <div style={{ textAlign: "right", paddingTop: 10, paddingRight: 10, color: "grey" }}>
                  {t("packages.incInactive")}
                </div>
              </div>
            </Fragment>
          )}

          {hasPermission("bookingManagement.saleItems.create") && (treeState.selectedRecord == null || (treeState.selectedRecord != null && treeState.selectedRecord.id != -1)) && (
            <div style={flex.flexAuto}>
              <Button style={{ marginLeft: 10, marginTop: 5 }} variant="contained" color="secondary" data-testid="products-new-product-button" onClick={() => {
                setParent(null);
                setTreeState({
                  ...treeState,
                  selectedRecord: {
                    hasChildren: false,
                    id: -1,
                    parentId: 0,
                    name: "New Product",
                    description: "",
                    maxQty: 0,
                    stdRate: 0,
                    vat: session.current.site == null ? 0 : session.current.site.preferences.vat_rate,
                    active: true
                  } as Product
                });
              }}><FiPlus style={{ marginRight: 10 }} />
                {t("booking.labels.product")}
              </Button>
            </div>
          )}

          {hasPermission("bookingManagement.saleItems.create") && (treeState.selectedRecord != null && treeState.selectedRecord.id != -1) && (
            <div style={flex.flexAuto}>
              <Button
                style={{ marginLeft: 10, marginTop: 5 }}
                variant="contained"
                data-testid="products-new-sub-product-button"
                onClick={() => {
                  let parentId = 0;
                  let newParent: Product;
                  if (treeState.selectedRecord != null) {
                    parentId = treeState.selectedRecord.id;
                    newParent = treeState.selectedRecord as Product;

                    setParent(newParent);

                    setTreeState({
                      ...treeState,
                      selectedRecord: {
                        hasChildren: false,
                        id: -1,
                        parentId: parentId,
                        name: t("products.new"),
                        description: newParent.description,
                        maxQty: newParent.maxQty,
                        maxQtyPerDay: newParent.maxQtyPerDay,
                        stdRate: newParent.stdRate,
                        productType: newParent.productType,
                        vat: newParent.vat,
                        active: true
                      } as Product
                    });
                  }


                }}><FiPlus style={{ marginRight: 10 }}></FiPlus>
                {t("products.subProduct")}
              </Button>
            </div>
          )}

          {hasPermission("bookingManagement.saleItems.delete") && (treeState.selectedRecord != null && treeState.selectedRecord.id != -1) && (
            <div style={flex.flexAuto}>
              <IconButton
                style={{ marginLeft: 10 }}
                onClick={() => {
                  setShowDeleteProduct(true);
                }}
                data-testid="products-delete-product-button"
              >
                <FiTrash />
              </IconButton>
            </div>
          )}
        </div>
      </div>
      <div>
        <div style={flex.flexRows}>
          <div style={treeState.selectedRecord != null ? flex.flexAuto : flex.flexFill}>

            <NotchedCard label={t("booking.labels.products")}>
              <div style={{ width: 300, overflowX: "auto", padding: 20 }}>
                <TreeViewContext.Provider value={{
                  state: treeState,
                  setState: (updatedState) => {
                    setTreeState(updatedState);
                    if (updatedState.selectedRecord) {
                      const product = updatedState.selectedRecord as Product;
                      if (product.id != -1) {
                        setParent(null);
                      }
                    }
                  },
                  treeViewLoader: ProductsTreeViewLoader,
                  getNodeDesign: (record) => {
                    const product = record as Product;
                    if (product.hasChildren == false) {
                      return {
                        colour: "green",
                        icon: FiFolder
                      };
                    } else {
                      return {
                        colour: "grey",
                        icon: FiFolder
                      };
                    }
                  },

                }}>
                  <TreeView></TreeView>
                </TreeViewContext.Provider>
              </div>

            </NotchedCard>

          </div>
          {treeState.selectedRecord && (
            <div style={flex.flexFill}>
              <div style={{ paddingLeft: 10 }}>
                <NotchedCard label={treeState.selectedRecord.id == -1 ? (parent == null ? t("products.new") : t("products.newSubProduct")) : t("products.editProduct")}>
                  {treeState.selectedRecord != null && (
                    <EditableGroup<Product>
                      id="product"
                      testId="product-manager-group"
                      forceEdit={treeState.selectedRecord.id == -1}
                      editType={editMode}
                      data={treeState.selectedRecord as Product}
                      onDataChanged={async (data) => save(data)}
                      onCancelEdit={() => {
                        if (treeState.selectedRecord) {
                          if (treeState.selectedRecord.id == -1) {
                            setTreeState({ ...treeState, selectedRecord: null });
                            setProductType(ProductTypes.default);
                          } else {
                            setProductType(((treeState.selectedRecord) as Product).productType);
                          }
                        }
                      }}
                    >
                      <div style={{ marginBottom: 50 }}>
                        <Grid container>
                          {(parent && (
                            <>
                              <EditableField<Product, string>
                                inputType={EditableFieldTypes.text}
                                label={t("products.newSubProductOf")}
                                onProvide={() => parent.name}
                                readOnly={true}
                                data-testid="products-new-sub-product-of-field"
                              />
                              <EditableField<Product, string>
                                inputType={EditableFieldTypes.empty}
                                label={""}
                              />
                            </>
                          ))}
                          <EditableField<Product, string>
                            inputType={EditableFieldTypes.text}
                            label={t("input.labels.productName")}
                            onProvide={(data) => data.name}
                            onChange={(data, value) => data.name = value}
                            data-testid="products-product-name-field"
                          />
                          {treeState.selectedRecord.hasChildren == false && !isAdditionalTaxRatesReleased && singleTaxFields}

                          {treeState.selectedRecord.hasChildren == false && isAdditionalTaxRatesReleased && additionalTaxFields}
                        </Grid>
                      </div>

                    </EditableGroup>
                  )}

                </NotchedCard>
              </div>
            </div>)
          }

        </div>

      </div>
    </div >
  );
}

export default ProductManager;
